/**
 * Names Pickeasy's two fields to a screen reader.
 *
 * The day and the Janela de entrega — `#neo-input-date` and `#neo-input-time` — ship with
 * a placeholder and nothing else: no `<label for>`, no `aria-label`, no
 * `aria-labelledby`. A placeholder is the last thing the accessible-name computation
 * falls back to, so the fields do have a name until a value arrives, and then it is
 * gone: the moment a Comprador picks Saturday, the field that said "Escolha o dia"
 * announces itself as nothing at all. A customer who navigates back up the form to
 * check what they chose is told "edit text, 13/09/2026" with no word for which question
 * that answers.
 *
 * axe does not report this, which is why it needed reading by hand. Its `label` rule
 * accepts a placeholder as a name source, because per the accessible-name spec that is
 * exactly what it is. The rule is right and the field is still wrong.
 *
 * So this copies each placeholder — Marina's own words, already in Portuguese — into a
 * real `aria-label`, which does not evaporate when the field fills. Nothing visual
 * changes and no behaviour changes; the placeholder stays where it is.
 *
 * Why a script at all, on a page whose whole point is that the theme draws none of it:
 * these fields do not exist in any markup we own. Pickeasy renders them from its own
 * bundle after its configuration arrives — about 1.2 seconds after the page on the live
 * cart, per `tests/cart.spec.mjs` — so a Liquid attribute has nothing to attach to and
 * a stylesheet cannot express a name. This is the smallest thing that can.
 *
 * Loaded from `snippets/scripts.liquid`, gated on `request.page_type == 'cart'`.
 * Recorded in `theme/UPSTREAM.md` (carrinho-entrega 40).
 */

/**
 * The fields to name, and where each one's name comes from.
 *
 * The selectors are Pickeasy's, so they are the app's to rename on any release. That is
 * survivable in the direction that matters: if one stops matching, the field goes back
 * to being named by its placeholder, which is what it ships as. Nothing breaks, one
 * improvement lapses, and `tests/cart.spec.mjs` is where that would be caught.
 *
 * `.additional-details-input` and `#delivery-location-zip` are here because the app
 * draws them in states this cart can reach — the additional-details toggle and the
 * Retirada search — and both carry a placeholder on the same terms.
 */
const FIELDS = [
  '#neo-input-date',
  '#neo-input-time',
  '.additional-details-input',
  '.pickup-location-search',
  '#delivery-location-zip',
];

/**
 * Whether the element already has a name that will outlive its value.
 *
 * A placeholder deliberately does not count, since it is the thing being replaced. An
 * `aria-label` or a real `<label for>` does, and either means the app has fixed this
 * itself and we should keep our hands off it.
 *
 * @param {Element} field
 * @returns {boolean}
 */
function isNamed(field) {
  if (field.getAttribute('aria-label')?.trim()) return true;
  if (field.getAttribute('aria-labelledby')?.trim()) return true;
  if (field.closest('label')) return true;
  if (field.id && document.querySelector(`label[for="${CSS.escape(field.id)}"]`)) return true;
  return false;
}

/** Copies the placeholder into an `aria-label`, once, for anything still unnamed. */
function nameFields() {
  for (const selector of FIELDS) {
    for (const field of document.querySelectorAll(selector)) {
      if (isNamed(field)) continue;

      const placeholder = field.getAttribute('placeholder')?.trim();
      if (!placeholder) continue;

      field.setAttribute('aria-label', placeholder);
    }
  }
}

/**
 * Watches for the app drawing, and for it drawing again.
 *
 * One pass on load would miss everything: the fields arrive with the app's
 * configuration, and they are re-rendered when the customer switches between Entrega
 * and Retirada, when the month changes, and when the slot list refreshes. The observer
 * is the whole lifetime of the page rather than a poll with a timeout, because there is
 * no moment after which the app is finished.
 *
 * `subtree` on `document.body` is broad, and it is the right breadth: Pickeasy mounts
 * its popup as a direct child of `<body>`, outside whatever container its widget sits
 * in, so a narrower root would miss the search field inside it. The callback is a
 * handful of `querySelectorAll` calls over a small page and it does nothing on the runs
 * where nothing matched.
 */
function watch() {
  nameFields();

  new MutationObserver(nameFields).observe(document.body, {
    childList: true,
    subtree: true,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watch, { once: true });
} else {
  watch();
}
